import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Slider,
  Divider,
  Chip
} from "@mui/material";
import { ChevronUp, ChevronDown } from "lucide-react";
import ColorRow from "./ColorRow";

export default function DesignCard({ templateState, setTemplateState, errors, isUni = false }) {

  const handleChange = (key, value) => {
    setTemplateState((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setTemplateState((prev) => ({
        ...prev,
        logoFile: file,
        logoUrl: URL.createObjectURL(file),
      }));
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setTemplateState((prev) => ({
      ...prev,
      logoUrl: url,
      logoFile: null,
    }));
  };

  const handleClearLogo = () => {
    setTemplateState((prev) => ({
      ...prev,
      logoFile: null,
      logoUrl: "",
    }));
  };

  const OptionButton = ({ label, selected, onClick }) => (
  <Button
    onClick={onClick}
    variant={selected ? "contained" : "outlined"}
    color={selected ? "primary" : "inherit"}
    fullWidth
    sx={{
      py: 1,
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: 2,
      borderColor: selected ? 'primary.main' : 'grey.300',
      backgroundColor: selected ? 'primary.main' : 'white',
      color: selected ? 'white' : 'grey.700', // Fixed: explicit colors
      '&:hover': {
        backgroundColor: selected ? 'primary.dark' : 'grey.100', // Fixed hover states
        borderColor: selected ? 'primary.main' : 'grey.400'
      }
    }}
  >
    {label}
  </Button>
);

  const customFieldKeys = (templateState.customFields || []).map(f => f.key);
  const baseHeaderKeys = ["name", "email", "phone", "address", ...customFieldKeys];
  const baseFooterKeys = ["name", "email", "phone", "address", "cin", "gst", ...customFieldKeys];

  const getOrderedList = (savedOrder, baseKeys) => {
    const order = Array.isArray(savedOrder) ? savedOrder : [];
    const validSavedOrder = order.filter(key => baseKeys.includes(key));
    const newKeys = baseKeys.filter(key => !validSavedOrder.includes(key));
    return [...validSavedOrder, ...newKeys];
  };

  const headerOptions = getOrderedList(templateState.headerFieldOrder, baseHeaderKeys);
  const footerOptions = getOrderedList(templateState.footerFieldOrder, baseFooterKeys);

  const handleMove = (area, index, direction) => {
    const fieldKey = area === 'header' ? 'headerFieldOrder' : 'footerFieldOrder';
    const currentList = area === 'header' ? [...headerOptions] : [...footerOptions];

    if (direction === 'up' && index > 0) {
      [currentList[index - 1], currentList[index]] = [currentList[index], currentList[index - 1]];
    } else if (direction === 'down' && index < currentList.length - 1) {
      [currentList[index + 1], currentList[index]] = [currentList[index], currentList[index + 1]];
    }

    setTemplateState(prev => ({ ...prev, [fieldKey]: currentList }));
  };

  const getLabel = (key) => {
    if (key === "name") return isUni ? "University Name" : "Business Name";
    if (key === "cin") return isUni ? "Registration Number" : "Registration/License Number";
    if (key === "gst") return isUni ? "GST Number" : "GST Number";
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  const toggleFieldVisibility = (area, fieldId) => {
    const targetKey = area === 'header' ? 'showCompanyFields' : 'showFooterFields';
    setTemplateState(prev => ({
      ...prev,
      [targetKey]: {
        ...prev[targetKey],
        [fieldId]: !prev[targetKey]?.[fieldId]
      }
    }));
  };

  const renderReorderableList = (area, orderArray) => {
    const showState = templateState[area === 'header' ? 'showCompanyFields' : 'showFooterFields'] || {};

    return (
      <Box sx={{ mt: 3 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            color: 'grey.500',
            textTransform: 'uppercase',
            letterSpacing: 1,
            display: 'block',
            mb: 2
          }}
        >
          Select items to show in {area}:
        </Typography>

        {orderArray.map((fieldKey, index) => (
          <Box
            key={`${area}-${fieldKey}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              bgcolor: 'white',
              border: '1px solid',
              borderColor: showState[fieldKey] ? 'primary.200' : 'grey.200',
              px: 2,
              py: 1.5,
              borderRadius: 2,
              transition: 'all 0.2s',
              opacity: showState[fieldKey] ? 1 : 0.8,
              '&:hover': {
                opacity: 1,
                borderColor: showState[fieldKey] ? 'primary.300' : 'grey.300'
              },
              mb: 1.5
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!showState[fieldKey]}
                  onChange={() => toggleFieldVisibility(area, fieldKey)}
                  sx={{
                    color: 'grey.400',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: showState[fieldKey] ? 'grey.900' : 'grey.600'
                  }}
                >
                  {getLabel(fieldKey)}
                </Typography>
              }
              sx={{ flex: 1, m: 0 }}
            />

            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                onClick={(e) => { e.preventDefault(); handleMove(area, index, 'up'); }}
                disabled={index === 0}
                size="small"
                sx={{
                  color: 'grey.500',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'primary.50'
                  },
                  '&.Mui-disabled': {
                    opacity: 0.3,
                    cursor: 'not-allowed'
                  }
                }}
              >
                <ChevronUp size={16} strokeWidth={2.5} />
              </IconButton>

              <IconButton
                onClick={(e) => { e.preventDefault(); handleMove(area, index, 'down'); }}
                disabled={index === orderArray.length - 1}
                size="small"
                sx={{
                  color: 'grey.500',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'primary.50'
                  },
                  '&.Mui-disabled': {
                    opacity: 0.3,
                    cursor: 'not-allowed'
                  }
                }}
              >
                <ChevronDown size={16} strokeWidth={2.5} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const currentLogoSize = Number(templateState.logoSize) || 80;
  const minLogoSize = 10;
  const maxLogoSize = 250;
  console.log("currentLogoSizeMMM", currentLogoSize)

  return (
    <Card
      sx={{
        bgcolor: 'white',
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 3,
        p: 0,
        boxShadow: 1
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: 'grey.900' }}>
            Design
          </Typography>

          {/* Header Color */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={templateState.showHeaderBg}
                  onChange={(e) => handleChange("showHeaderBg", e.target.checked)}
                  sx={{
                    color: 'grey.400',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>
                  Show header background color
                </Typography>
              }
            />

            {templateState.showHeaderBg && (
              <Box sx={{ mt: 2 }}>
                <ColorRow
                  value={templateState.headerColor}
                  onChange={(c) => handleChange("headerColor", c)}
                />
              </Box>
            )}
          </Box>

          {/* Footer Color */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700', mb: 2 }}>
              Footer Color
            </Typography>
            <ColorRow
              value={templateState.footerColor}
              onChange={(c) => handleChange("footerColor", c)}
            />
          </Box>

          {/* Logo Management */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'grey.800', mb: 2 }}>
              Logo (optional)
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Upload Image */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 1 }}>
                  Upload Image
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  p: 1,
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': {
                    borderColor: 'grey.400'
                  }
                }}>
                  <Button
                    variant="contained"
                    component="label"
                    sx={{
                      bgcolor: 'grey.100',
                      color: 'grey.800',
                      fontWeight: 'bold',
                      mr: 2,
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'grey.200'
                      }
                    }}
                  >
                    Choose File
                    <input
                      type="file"
                      hidden
                      accept="image/png, image/jpeg, image/gif, image/webp"
                      onChange={handleFileChange}
                    />
                  </Button>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      fontWeight: 500,
                      color: templateState.logoFile ? 'primary.main' : 'grey.400',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {templateState.logoFile ? (
                      <>📄 {templateState.logoFile.name}</>
                    ) : (
                      'no file selected'
                    )}
                  </Typography>
                </Box>
              </Box>

              {/* Or Enter URL */}
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'grey.600', display: 'block', mb: 1 }}>
                  Or Enter URL
                </Typography>
                <TextField
                  fullWidth
                  placeholder="https://example.com/logo.png"
                  value={templateState.logoUrl || ""}
                  onChange={handleUrlChange}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      boxShadow: 1,
                      '&:hover fieldset': {
                        borderColor: 'primary.main'
                      }
                    }
                  }}
                />
              </Box>

              {/* Clear Logo Button */}
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClearLogo}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: 'grey.300',
                  color: 'grey.600',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: 'grey.400',
                    bgcolor: 'grey.50',
                    color: 'grey.800'
                  }
                }}
              >
                Clear Logo
              </Button>

              {/* Logo Size Slider */}
              <Box sx={{ pt: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.600' }}>
                    Logo size
                  </Typography>
                  <Chip
                    label={`${currentLogoSize}px`}
                    size="small"
                    sx={{
                      bgcolor: 'primary.50',
                      color: 'primary.700',
                      fontWeight: 'bold',
                      border: '1px solid',
                      borderColor: 'primary.200',
                      borderRadius: 1
                    }}
                  />
                </Box>

                <Slider
                  value={currentLogoSize}
                  onChange={(e, value) => handleChange("logoSize", value)}
                  min={minLogoSize}
                  max={maxLogoSize}
                  sx={{
                    color: 'primary.main',
                    height: 8,
                    '& .MuiSlider-thumb': {
                      height: 18,
                      width: 18,
                      backgroundColor: 'primary.main',
                      boxShadow: '0 0 0 2px white, 0 2px 5px rgba(0,0,0,0.2)',
                      '&:hover, &.Mui-focusVisible': {
                        boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.2)'
                      }
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: 'primary.main'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'grey.200'
                    }
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: 'grey.400', fontWeight: 500 }}>
                    10px
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'grey.400', fontWeight: 500 }}>
                    250px
                  </Typography>
                </Box>
              </Box>

              {/* Header Layout */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.600', mb: 1.5 }}>
                  Header Layout
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <OptionButton
                    label="Stacked"
                    selected={templateState.headerLayout === 'stacked' || !templateState.headerLayout}
                    onClick={() => handleChange("headerLayout", 'stacked')}
                  />
                  <OptionButton
                    label="Logo Left"
                    selected={templateState.headerLayout === 'logo-left'}
                    onClick={() => handleChange("headerLayout", 'logo-left')}
                  />
                  <OptionButton
                    label="Logo Right"
                    selected={templateState.headerLayout === 'logo-right'}
                    onClick={() => handleChange("headerLayout", 'logo-right')}
                  />
                </Box>
              </Box>

              {/* Logo Position (only for stacked layout) */}
              {(!templateState.headerLayout || templateState.headerLayout === 'stacked') && (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.600', mb: 1.5 }}>
                    Logo Position
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <OptionButton
                      label="Left"
                      selected={templateState.logoAlignment === 'left'}
                      onClick={() => handleChange("logoAlignment", 'left')}
                    />
                    <OptionButton
                      label="Center"
                      selected={templateState.logoAlignment === 'center'}
                      onClick={() => handleChange("logoAlignment", 'center')}
                    />
                    <OptionButton
                      label="Right"
                      selected={templateState.logoAlignment === 'right'}
                      onClick={() => handleChange("logoAlignment", 'right')}
                    />
                  </Box>
                </Box>
              )}

              {/* Text Alignment */}
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.600', mb: 1.5 }}>
                  Text Alignment
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <OptionButton
                    label="Left"
                    selected={templateState.textAlignment === 'left'}
                    onClick={() => handleChange("textAlignment", 'left')}
                  />
                  <OptionButton
                    label="Center"
                    selected={templateState.textAlignment === 'center'}
                    onClick={() => handleChange("textAlignment", 'center')}
                  />
                  <OptionButton
                    label="Right"
                    selected={templateState.textAlignment === 'right'}
                    onClick={() => handleChange("textAlignment", 'right')}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Header Company Info Toggle */}
          <Box sx={{
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            p: 3,
            bgcolor: 'grey.50'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={templateState.showHeaderInfo !== false}
                  onChange={(e) => setTemplateState(prev => ({ ...prev, showHeaderInfo: e.target.checked }))}
                  sx={{
                    color: 'grey.400',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.900' }}>
                  Show {isUni ? "university" : "business"} info in header
                </Typography>
              }
            />
            {templateState.showHeaderInfo !== false && renderReorderableList('header', headerOptions)}
          </Box>

          {/* Footer Company Info Toggle */}
          <Box sx={{
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 2,
            p: 3,
            bgcolor: 'grey.50'
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={templateState.showFooterInfo !== false}
                  onChange={(e) => setTemplateState(prev => ({ ...prev, showFooterInfo: e.target.checked }))}
                  sx={{
                    color: 'grey.400',
                    '&.Mui-checked': {
                      color: 'primary.main'
                    }
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.900' }}>
                  Show {isUni ? "university" : "business"} info in footer
                </Typography>
              }
            />
            {templateState.showFooterInfo !== false && renderReorderableList('footer', footerOptions)}
          </Box>

          {/* Footer Text */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700', mb: 1.5 }}>
              Footer Text
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={isUni ? "e.g., © 2024 Your University. All rights reserved." : "e.g., © 2024 Your Company. All rights reserved."}
              value={templateState.footerText}
              onChange={(e) => handleChange("footerText", e.target.value)}
              error={!!errors?.footerText}
              helperText={errors?.footerText}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: 'primary.main'
                  }
                }
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}